import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { MdClose } from "react-icons/md";
import { Wallet2 } from "lucide-react";
import {
  TbCurrencyRupee,
  TbLoader2,
  TbCheck,
  TbAlertCircle,
  TbRefresh,
} from "react-icons/tb";
import toast from "react-hot-toast";
import axios from "../../../Utils/axiosInstance";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000];

const RechargeModal = ({ isOpen, onClose, wallet, user, onSuccess }) => {
  // console.log("wallet", wallet);
  // console.log("user", user);
  const [animate, setAnimate] = useState(false);
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("input"); // "input" | "processing" | "success" | "failed"
  const [loading, setLoading] = useState(false);
  const [lastTxn, setLastTxn] = useState(null);
  const [failReason, setFailReason] = useState("");

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      setAmount("");
      setStep("input");
      setLoading(false);
      setLastTxn(null);
      setFailReason("");
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (step === "processing") return;
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleRecharge = async () => {
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount < 1) {
      toast.error("Minimum recharge amount is ₹1");
      return;
    }
    if (parsedAmount > (wallet?.dailyRechargeLimit || 100000)) {
      toast.error(
        `Daily recharge limit is ₹${(wallet?.dailyRechargeLimit || 100000).toLocaleString("en-IN")}`,
      );
      return;
    }

    setLoading(true);
    setFailReason("");

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setLoading(false);
        setFailReason(
          "Could not load payment gateway. Check your internet connection.",
        );
        setStep("failed");
        return;
      }

      let orderData;
      try {
        const orderRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/recharge/initiate-recharge`,
          { amount: parsedAmount },
        );
        if (!orderRes.data.success) {
          setLoading(false);
          setFailReason(
            orderRes.data.message || "Failed to create payment order.",
          );
          setStep("failed");
          return;
        }
        orderData = orderRes.data.data;
      } catch (err) {
        setLoading(false);
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to initiate recharge. Please try again.";
        setFailReason(msg);
        setStep("failed");
        return;
      }

      const { orderId, razorpayKeyId, orderDetails } = orderData;

      setLoading(false);
      setStep("processing");

      const options = {
        key: razorpayKeyId,
        amount: orderDetails.amount,
        currency: orderDetails.currency,
        name: "Wallet Recharge",
        description: `Add ₹${parsedAmount.toLocaleString("en-IN")} to your wallet`,
        order_id: orderId,
        prefill: {
          name:
            user?.fullName ||
            `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: { color: "#4F46E5" },
        modal: {
          ondismiss: () => {
            setStep("input");
            toast("Payment cancelled", { icon: "ℹ️" });
          },
        },
        handler: async (response) => {
          setStep("processing");
          try {
            const verifyRes = await axios.post(
              `${process.env.REACT_APP_API_URL}/recharge/verify-payments`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            );
            if (verifyRes.data.success) {
              setLastTxn(verifyRes.data.data);
              setStep("success");
              onSuccess && onSuccess();
            } else {
              setFailReason(
                verifyRes.data.message ||
                "Payment was received but verification failed. Contact support with your payment ID.",
              );
              setStep("failed");
            }
          } catch (err) {
            const msg =
              err.response?.data?.message ||
              "Payment verification failed. If money was deducted, contact support.";
            setFailReason(msg);
            setStep("failed");
          }
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", (response) => {
        const reason =
          response.error?.description ||
          response.error?.reason ||
          "Payment was declined by your bank or payment provider.";
        setFailReason(reason);
        setStep("failed");
      });

      rzp.open();
    } catch (err) {
      setLoading(false);
      setFailReason("An unexpected error occurred. Please try again.");
      setStep("failed");
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== "processing") handleClose();
      }}
    >
      <div
        className={`bg-white dark:bg-gray-900 w-full md:w-[480px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${
          animate
            ? "scale-100 translate-y-0"
            : "scale-95 md:translate-y-4 translate-y-full"
        }`}
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center justify-between flex-shrink-0">
          <button
            type="button"
            disabled={step === "processing"}
            onClick={handleClose}
            className="text-2xl leading-none text-white/90 hover:text-white transition disabled:opacity-30 cursor-pointer"
          >
            ←
          </button>
          <div className="flex-1 text-center font-semibold text-lg text-white">
            {step === "success"
              ? "Recharge Successful"
              : step === "failed"
              ? "Recharge Failed"
              : step === "processing"
              ? "Processing Payment"
              : "Add Money to Wallet"}
          </div>
          {step !== "processing" ? (
            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition cursor-pointer"
            >
              <MdClose size={18} />
            </button>
          ) : (
            <div className="w-8" />
          )}
        </div>

        {/* SCROLLABLE BODY */}
        <div className="overflow-y-auto flex-1">
        {/* ────────────────── INPUT STEP ────────────────── */}
        {step === "input" && (
          <div className="p-5">
            {/* Wallet Card — exact WalletModal design */}
            <div className="relative shineEffect rounded-2xl p-6 overflow-hidden text-white shadow-lg mb-5">
              {/* bg layers */}
              <div className="absolute inset-0 bg-gradient-to-br from-primaryColor via-subPrimaryColor to-[#5a1828]" />
              <div className="absolute -bottom-12 -right-12 w-52 h-52 rounded-full bg-white/5 blur-3xl pointer-events-none" />
              <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-primaryFadedColor/15 blur-2xl pointer-events-none" />
              {/* decorative circles */}
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full border border-white/10 translate-x-10 -translate-y-10 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full border border-white/10 -translate-x-6 translate-y-6 pointer-events-none" />

              <div className="relative z-10">
                {/* top row */}
                <div className="flex items-center justify-between mb-7">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center">
                      <Wallet2 size={14} />
                    </div>
                    <span className="text-[11px] font-semibold tracking-widest text-white/65 uppercase">
                      Wallet
                    </span>
                  </div>
                </div>

                {/* balance */}
                <p className="text-[11px] text-white/60 mb-1 tracking-wide font-medium">
                  Current Balance
                </p>
                <h2 className="text-[2.2rem] font-bold tracking-tight leading-none">
                  ₹
                  {Number(wallet?.availableBalance ?? 0).toLocaleString(
                    "en-IN",
                  )}
                </h2>

                {/* details */}
                {/* <div className="mt-6 pt-4 border-t border-white/15 grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">
                      Total
                    </p>
                    <p className="text-xs font-semibold">
                      ₹{Number(wallet?.balance ?? 0).toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">
                      Reserved
                    </p>
                    <p className="text-xs font-semibold">
                      ₹
                      {Number(wallet?.reservedAmount ?? 0).toLocaleString(
                        "en-IN",
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">
                      Daily Limit
                    </p>
                    <p className="text-xs font-semibold">
                      ₹
                      {Number(
                        wallet?.dailyRechargeLimit ?? 100000,
                      ).toLocaleString("en-IN")}
                    </p>
                  </div>
                </div> */}
              </div>
            </div>

            {/* Amount input */}
            <div className="relative mb-4">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <TbCurrencyRupee
                  size={24}
                  className="text-primaryColor font-bold"
                />
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleRecharge()}
                placeholder="0"
                min={1}
                className="w-full pl-10 pr-4 py-4 text-3xl font-bold rounded-2xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-200 focus:outline-none focus:border-primaryColor transition"
              />
            </div>

            {/* Quick amounts */}
            <div className="grid grid-cols-5 gap-2 mb-5">
              {QUICK_AMOUNTS.map((q) => (
                <button
                  key={q}
                  onClick={() => setAmount(String(q))}
                  className={`py-2 rounded-xl text-xs font-semibold border transition ${Number(amount) === q
                    ? "bg-primaryColor text-white border-primaryColor shadow"
                    : "bg-gray-50 border-gray-200 text-gray-600 hover:border-primaryColor/50"
                    }`}
                >
                  ₹{q >= 1000 ? `${q / 1000}K` : q}
                </button>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={handleRecharge}
              disabled={loading || !amount || parseFloat(amount) < 1}
              className="w-full mt-1 bg-primaryColor text-white py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <TbLoader2 size={18} className="animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <TbCurrencyRupee size={18} />
                  {amount && parseFloat(amount) >= 1
                    ? `Pay ₹${parseFloat(amount).toLocaleString("en-IN")}`
                    : "Proceed to Pay"}
                </>
              )}
            </button>

            <button
              onClick={onClose}
              className="w-full mt-2 border py-3 rounded-xl text-sm font-medium text-gray-600"
            >
              Cancel
            </button>

            <p className="text-center text-[10px] text-primaryColor font-semibold mt-3">
              🔒 Secured by Razorpay · UPI · Cards · Netbanking
            </p>
          </div>
        )}

        {/* ────────────────── PROCESSING STEP ────────────────── */}
        {step === "processing" && (
          <div className="p-5">
            <div className="text-center mb-4">
              <h2 className="font-bold text-lg text-primaryColor">
                Processing
              </h2>
            </div>

            {/* Wallet card — same design, dimmed */}
            <div className="relative rounded-2xl p-6 overflow-hidden text-white shadow-lg mb-5 opacity-70">
              <div className="absolute inset-0 bg-gradient-to-br from-primaryColor via-subPrimaryColor to-[#5a1828]" />
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full border border-white/10 translate-x-10 -translate-y-10 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full border border-white/10 -translate-x-6 translate-y-6 pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-7">
                  <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center">
                    <Wallet2 size={14} />
                  </div>
                  <span className="text-[11px] font-semibold tracking-widest text-white/65 uppercase">
                    Wallet
                  </span>
                </div>
                <p className="text-[11px] text-white/60 mb-1 tracking-wide font-medium">
                  Current Balance
                </p>
                <h2 className="text-[2.2rem] font-bold tracking-tight leading-none">
                  ₹
                  {Number(wallet?.availableBalance ?? 0).toLocaleString(
                    "en-IN",
                  )}
                </h2>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-center py-4">
              <TbLoader2
                size={36}
                className="text-primaryColor animate-spin mb-4"
              />
              <h3 className="font-bold text-base text-gray-900 mb-1">
                Processing Payment
              </h3>
              <p className="text-sm text-gray-500">
                Please complete the payment in the Razorpay window.
                <br />
                Do not close or refresh this page.
              </p>
            </div>
          </div>
        )}

        {/* ────────────────── SUCCESS STEP ────────────────── */}
        {step === "success" && (
          <div className="p-5">
            <div className="text-center mb-4">
              <h2 className="font-bold text-lg text-primaryColor">
                Your Wallet
              </h2>
            </div>

            {/* Wallet card — updated balance */}
            <div className="relative shineEffect rounded-2xl p-6 overflow-hidden text-white shadow-lg mb-5">
              <div className="absolute inset-0 bg-gradient-to-br from-primaryColor via-subPrimaryColor to-[#5a1828]" />
              <div className="absolute -bottom-12 -right-12 w-52 h-52 rounded-full bg-white/5 blur-3xl pointer-events-none" />
              <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-primaryFadedColor/15 blur-2xl pointer-events-none" />
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full border border-white/10 translate-x-10 -translate-y-10 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full border border-white/10 -translate-x-6 translate-y-6 pointer-events-none" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-7">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center">
                      <Wallet2 size={14} />
                    </div>
                    <span className="text-[11px] font-semibold tracking-widest text-white/65 uppercase">
                      Wallet
                    </span>
                  </div>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full border bg-green-500/20 border-green-400/30 text-green-300">
                    Recharged
                  </span>
                </div>

                <p className="text-[11px] text-white/60 mb-1 tracking-wide font-medium">
                  New Balance
                </p>
                <h2 className="text-[2.2rem] font-bold tracking-tight leading-none">
                  ₹
                  {Number(
                    lastTxn?.newBalance ?? wallet?.availableBalance ?? 0,
                  ).toLocaleString("en-IN")}
                </h2>

                {lastTxn && (
                  <div className="mt-6 pt-4 border-t border-white/15 grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">
                        Added
                      </p>
                      <p className="text-xs font-semibold text-green-300">
                        +₹{Number(lastTxn.amount ?? 0).toLocaleString("en-IN")}
                      </p>
                    </div>
                    {lastTxn.paymentId && (
                      <div className="col-span-2">
                        <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">
                          Payment ID
                        </p>
                        <p className="text-[10px] font-mono font-semibold truncate">
                          {lastTxn.paymentId}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Success indicator */}
            <div
              className="flex items-center justify-center gap-3 mb-5 px-4 py-3 rounded-2xl bg-green-50 border border-green-200"
              style={{
                animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
              }}
            >
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <TbCheck size={18} className="text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-green-700">
                  Payment Successful!
                </p>
                <p className="text-xs text-green-600">
                  Your wallet has been recharged
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="w-full bg-primaryColor text-white py-3 rounded-xl font-semibold cursor-pointer hover:bg-primaryColor/90 transition shadow-sm"
            >
              Done
            </button>
          </div>
        )}

        {/* ────────────────── FAILED STEP ────────────────── */}
        {step === "failed" && (
          <div className="p-5">
            <div className="text-center mb-4">
              <h2 className="font-bold text-lg text-primaryColor">
                Your Wallet
              </h2>
            </div>

            {/* Wallet card — same design */}
            <div className="relative shineEffect rounded-2xl p-6 overflow-hidden text-white shadow-lg mb-5">
              <div className="absolute inset-0 bg-gradient-to-br from-primaryColor via-subPrimaryColor to-[#5a1828]" />
              <div className="absolute -bottom-12 -right-12 w-52 h-52 rounded-full bg-white/5 blur-3xl pointer-events-none" />
              <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-primaryFadedColor/15 blur-2xl pointer-events-none" />
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full border border-white/10 translate-x-10 -translate-y-10 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full border border-white/10 -translate-x-6 translate-y-6 pointer-events-none" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-7">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center">
                      <Wallet2 size={14} />
                    </div>
                    <span className="text-[11px] font-semibold tracking-widest text-white/65 uppercase">
                      Wallet
                    </span>
                  </div>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full border bg-red-500/20 border-red-400/30 text-red-300">
                    Unchanged
                  </span>
                </div>

                <p className="text-[11px] text-white/60 mb-1 tracking-wide font-medium">
                  Available Balance
                </p>
                <h2 className="text-[2.2rem] font-bold tracking-tight leading-none">
                  ₹
                  {Number(wallet?.availableBalance ?? 0).toLocaleString(
                    "en-IN",
                  )}
                </h2>

                <div className="mt-6 pt-4 border-t border-white/15 grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">
                      Total
                    </p>
                    <p className="text-xs font-semibold">
                      ₹{Number(wallet?.balance ?? 0).toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">
                      Reserved
                    </p>
                    <p className="text-xs font-semibold">
                      ₹
                      {Number(wallet?.reservedAmount ?? 0).toLocaleString(
                        "en-IN",
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 mb-0.5 uppercase tracking-wide">
                      Status
                    </p>
                    <p className="text-xs font-semibold text-red-300">Failed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Failure reason */}
            <div
              className="flex items-start gap-3 mb-5 px-4 py-3 rounded-2xl bg-red-50 border border-red-200"
              style={{
                animation: "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
              }}
            >
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <TbAlertCircle size={18} className="text-red-500" />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-red-700 mb-0.5">
                  Payment Failed
                </p>
                <p className="text-xs text-red-600 leading-relaxed">
                  {failReason ||
                    "Something went wrong. Your wallet was not charged."}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 border py-3 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setStep("input");
                  setFailReason("");
                }}
                className="flex-1 bg-primaryColor text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-primaryColor/90 transition shadow-sm cursor-pointer"
              >
                <TbRefresh size={16} />
                Try Again
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      <style>{`
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>
    </div>,
    document.body
  );
};

export default RechargeModal;
